import { Button } from '@neo/primitives/button'
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '@neo/primitives/dialog'
import { Typography } from '@neo/primitives/typography'
import { ShieldCheck } from 'lucide-react'
import type { FC } from 'react'
import { Link } from 'router-v6'

// Deep link to Phone Numbers with the KYC sheet already open.
//
// Must name the `list` tab explicitly. Bare `/phone-numbers` renders TabRedirect
// (configs/routes/main.tsx), which navigates to `${basePath}/${tab}` — a path
// with NO search string — so `?action=create` is silently dropped and the user
// lands on Phone Numbers with no sheet.
//
// `list` and not `compliance`: an org with no KYC and no numbers is in the
// first-run state, which renders the "Complete Your KYC" screen with no tab bar,
// so the KYC tab isn't reachable for them yet. IndiaComplianceEmptyState is what
// consumes ?action=create on that screen.
export const KYC_CREATE_PATH = '/phone-numbers/list?action=create'

interface KycRequiredModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
}

interface KycBeforeCreditsInput {
  /** Postpaid accounts settle invoices; that path must never be gated. */
  isPostpaid: boolean | undefined
  /** useIndiaComplianceFirstRun().isLoading */
  isLoading: boolean
  /** useIndiaComplianceFirstRun().isFirstRun — already India-scoped by the hook. */
  isFirstRun: boolean
}

/**
 * Whether Add Credits should divert to the KYC prompt instead of a payment flow.
 *
 * isFirstRun fails OPEN on anything unknown: it reads true while the numbers
 * and application counts are still in flight, so gating without the isLoading
 * check would flash the prompt at every India org, including ones already
 * approved. isPostpaid is the opposite direction — it requires a CONFIRMED
 * `false`, since an unresolved account query must never let a postpaid org
 * see this prompt, even for the instant before it resolves.
 */
export const shouldRequireKycBeforeCredits = ({
  isPostpaid,
  isLoading,
  isFirstRun,
}: KycBeforeCreditsInput): boolean => isPostpaid === false && !isLoading && isFirstRun

/**
 * Shown when an India org with no KYC application yet tries to add credits.
 * Credits can't be used until KYC clears, so this explains why and hands the
 * customer a link into the KYC flow rather than a dead-end button.
 */
export const KycRequiredModal: FC<KycRequiredModalProps> = ({ open, onOpenChange }) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    {/* Narrower than the DialogContent default (max-w-lg), matching the
        WhatsApp empty-state reference this layout is modelled on. That card is
        w-[285px], which is what makes its copy break at 4-5 words per line —
        max-w-md keeps the same proportions with a readable measure. */}
    <DialogContent className="max-w-md" data-testid="modal-kyc-required">
      <div className="flex flex-col gap-5">
        <div className="w-fit rounded-lg bg-primary/10 p-2.5">
          <ShieldCheck size={24} className="text-primary" />
        </div>
        <div className="space-y-2">
          <DialogTitle className="text-h3-500">Complete your KYC</DialogTitle>
          <DialogDescription asChild>
            <Typography variant="P/400" className="text-secondary">
              An approved application is required to add credits. This typically takes 10 to 15
              minutes.
            </Typography>
          </DialogDescription>
        </div>
        {/* A real link, so it can be opened in a new tab and reads as navigation.
            Dismissal is the close icon — no Cancel, so the panel has one action. */}
        <Button asChild className="w-full" data-testid="link-kyc-application">
          <Link to={KYC_CREATE_PATH}>Start KYC</Link>
        </Button>
      </div>
    </DialogContent>
  </Dialog>
)
